/**
 * How much one history entry affected (SRVR-0357 AC8–AC10): the number of notes, walls and effects it
 * added, changed or deleted, found by comparing the map as it stood before the entry with the map after it.
 * Pure; the History box runs it on hover.
 *
 * Objects carry no identity (SRVR-0081), so a "changed" object is a best match, not a proof: a removed and an
 * added object of one kind are paired when they share a time (and a hand or wall type, first), and when the
 * same number of objects of a kind are left on each side they pair in time order. A pair counts once.
 */
import type { Difficulty, FlashEvent, Obstacle } from '../../types/beatmap';

/** The parts of a history entry the comparison reads. */
export interface HistorySnapshot {
  difficulties: Record<string, Difficulty>;
  flashEvents?: FlashEvent[];
  effects?: Record<string, number[]>;
}

/** Objects affected by one entry. Notes include rails; walls are slides, crouches, triangles and squares
 *  (jumps are not counted); effects are the lights and the flash effects. */
export interface HistoryCounts { notes: number; walls: number; effects: number }

interface Item { key: string; time: number; sub: string }

const r3 = (v: number) => Math.round(v * 1000) / 1000;

// ── Items per kind ──────────────────────────────────────────────────────────

const noteItems = (d: Difficulty): Item[] => (d.notes ?? []).map(n => ({
  key: JSON.stringify([n.time_beats, n.position, n.note_type, n.hand, n.direction, n.has_rail, n.rail_nodes]),
  time: r3(n.time_beats), sub: n.hand,
}));

const slideItems = (d: Difficulty): Item[] => (d.slides ?? []).map(s => ({
  key: JSON.stringify([s.time_beats, s.slide_type, s.wall_type, s.position]),
  time: r3(s.time_beats), sub: s.wall_type,
}));

const obstacleItems = (list: Obstacle[] | undefined): Item[] => (list ?? []).map(o => ({
  key: JSON.stringify([o.time_beats, o.position, o.z_rotation]), time: r3(o.time_beats), sub: '',
}));

const msItems = (list: number[] | undefined): Item[] => (list ?? []).map(ms => ({ key: String(ms), time: ms, sub: '' }));

const beatItems = (list: number[] | undefined): Item[] => (list ?? []).map(b => ({ key: String(b), time: r3(b), sub: '' }));

const lightItems = (list: FlashEvent[]): Item[] => list.map(e => ({ key: JSON.stringify(e), time: r3(e.time_beats), sub: '' }));

// ── The comparison ──────────────────────────────────────────────────────────

/** Objects of one kind that are not the same on both sides, a removed and an added one that pair counting once. */
function affected(before: Item[], after: Item[]): number {
  const left = new Map<string, number>();
  for (const i of after) left.set(i.key, (left.get(i.key) ?? 0) + 1);
  const removed: Item[] = [];
  for (const i of before) {
    const n = left.get(i.key) ?? 0;
    if (n > 0) left.set(i.key, n - 1); else removed.push(i);
  }
  // `left` now holds the unmatched count per key; walk `after` again to pick those items.
  const added: Item[] = [];
  for (const i of after) {
    const n = left.get(i.key) ?? 0;
    if (n > 0) { left.set(i.key, n - 1); added.push(i); }
  }

  let pairs = 0;
  const pair = (match: (r: Item, a: Item) => boolean) => {
    for (let ri = 0; ri < removed.length; ri++) {
      const ai = added.findIndex(a => match(removed[ri], a));
      if (ai < 0) continue;
      pairs++;
      removed.splice(ri, 1);
      added.splice(ai, 1);
      ri--;
    }
  };
  pair((r, a) => r.time === a.time && r.sub === a.sub);
  pair((r, a) => r.time === a.time);
  // Whatever number is left on both sides pairs in time order.
  if (removed.length > 0 && removed.length === added.length) {
    pairs += removed.length;
    removed.length = 0;
    added.length = 0;
  }
  return pairs + removed.length + added.length;
}

const EMPTY: Difficulty = {
  name: '', note_count: 0, rail_count: 0, slide_count: 0, crouch_count: 0, jump_count: 0,
  duration_seconds: 0, duration_beats: 0, notes: [], slides: [], crouches: [], jumps: [],
};

/**
 * The notes, walls and effects `after` affects relative to `before`, over every difficulty and the lights.
 * A difficulty on one side only counts all its objects. Lights and effects are compared only when both
 * snapshots carry them.
 */
export function countHistoryChanges(before: HistorySnapshot, after: HistorySnapshot): HistoryCounts {
  const counts: HistoryCounts = { notes: 0, walls: 0, effects: 0 };
  const names = new Set([...Object.keys(before.difficulties), ...Object.keys(after.difficulties)]);
  for (const name of names) {
    const b = before.difficulties[name] ?? EMPTY;
    const a = after.difficulties[name] ?? EMPTY;
    if (b !== a) {
      counts.notes += affected(noteItems(b), noteItems(a));
      counts.walls += affected(slideItems(b), slideItems(a))
        + affected(msItems(b.crouches), msItems(a.crouches))
        + affected(obstacleItems(b.triangles), obstacleItems(a.triangles))
        + affected(obstacleItems(b.squares), obstacleItems(a.squares));
    }
    if (before.effects && after.effects) counts.effects += affected(beatItems(before.effects[name]), beatItems(after.effects[name]));
  }
  if (before.flashEvents && after.flashEvents) counts.effects += affected(lightItems(before.flashEvents), lightItems(after.flashEvents));
  return counts;
}
